// BTW-logica (spiegel van backend/btw.py). Vrijgesteld = alle € incl. BTW, geen blok.
// Plichtig = alle € excl. BTW; informatief blok: af te dragen − voorbelasting (− BTW over investeringen).
export const BTW_DEFAULT_RATE = 21;
export const BTW_RATE_MAX = 21;
export const BTW_RATE_OPTIONS = [
  { value: "", label: "Standaard (clubtarief)" },
  { value: "21", label: "21 %" },
  { value: "9", label: "9 %" },
  { value: "0", label: "Geen BTW" },
  { value: "custom", label: "Effectief %…" },
];
export const isCustomRate = (r) => r != null && ![21, 9, 0].includes(Number(r));
export const BTW_BLOCK_ROWS = [
  ["vat_revenue", "BTW af te dragen over inkomsten"],
  ["vat_opex", "Voorbelasting over kosten"],
  ["vat_balance", "BTW-saldo (schatting)"],
];
// Alleen boekjaren: ijkpunt — werkelijke afdracht volgens de aangiften (veld btw_afdracht_teruggave).
export const BTW_ACTUAL_ROWS = [
  ["vat_actual", "Werkelijke afdracht (aangiften)"],
  ["vat_diff", "Verschil schatting − werkelijk"],
];
export const BTW_SCENARIO_ROWS = [
  ["vat_revenue", "BTW af te dragen over inkomsten"],
  ["vat_opex", "Voorbelasting over kosten"],
  ["vat_capex", "BTW terug te vorderen over investeringen"],
  ["vat_balance", "BTW-saldo (schatting)"],
];
export const BTW_ESTIMATE_NOTE = "Schatting op basis van één tarief per subpost — uit totalen is niet af te leiden welk deel 9 % en welk deel 21 % is. Saldo + = te betalen, − = terug te vorderen. BTW loopt via de aangifte en beïnvloedt het exploitatieresultaat niet; het kwartaalritme van de afdracht is buiten beschouwing gelaten.";

export const BTW_REGIMES = ["vrijgesteld", "plichtig", "gemengd"];
export const BTW_AFTREK_OPTIONS = [
  { value: "", label: "Standaard (club)" },
  { value: "volledig", label: "Volledig aftrekbaar" },
  { value: "pro_rata", label: "Pro rata (haven)" },
  { value: "geen", label: "Geen aftrek" },
];
export const aftrekLabel = (v) => (BTW_AFTREK_OPTIONS.find((o) => o.value === v) || BTW_AFTREK_OPTIONS[0]).label;
export const btwRegime = (club) => (BTW_REGIMES.includes(club?.btw_regime) ? club.btw_regime : "vrijgesteld");

export function btwContext(club, subposts) {
  const regime = btwRegime(club);
  if (regime === "vrijgesteld") return null;
  const defaultRate = club?.btw_default_rate == null ? BTW_DEFAULT_RATE : Number(club.btw_default_rate);
  const defaultAftrek = ["volledig", "pro_rata", "geen"].includes(club?.btw_default_aftrek) ? club.btw_default_aftrek : "pro_rata";
  const proRata = regime === "gemengd" ? Math.min(100, Math.max(0, Number(club?.btw_pro_rata) || 0)) / 100 : 1;
  const rates = { revenue: {}, opex: {}, other_income: {} };
  const aftrek = {};
  for (const sp of subposts || []) {
    if (!rates[sp.category]) continue;
    rates[sp.category][sp.name] = sp.btw_rate == null ? defaultRate : Number(sp.btw_rate);
    if (sp.category === "opex") aftrek[sp.name] = ["volledig", "pro_rata", "geen"].includes(sp.btw_aftrek) ? sp.btw_aftrek : defaultAftrek;
  }
  return { regime, defaultRate, rates, aftrek, defaultAftrek, proRata };
}

export const aftrekFraction = (cat, ctx) => {
  if (!ctx || ctx.regime !== "gemengd") return 1;
  const c = ["volledig", "pro_rata", "geen"].includes(cat) ? cat : ctx.defaultAftrek;
  return c === "volledig" ? 1 : c === "geen" ? 0 : ctx.proRata;
};

const num = (v) => Number(v) || 0;
const vatOver = (items, rates, d) => (items || []).reduce((acc, it) => acc + num(it.amount) * (rates[it.name] ?? d) / 100, 0);
const sum = (items) => (items || []).reduce((acc, it) => acc + num(it.amount), 0);

export function btwForRow(row, ctx) {
  if (!row || !ctx) return null;
  const d = ctx.defaultRate;
  const rev = row.revenue_breakdown || [], oi = row.other_income_breakdown || [], opx = row.opex_breakdown || [];
  const inkomsten = num(row.revenue) + num(row.other_income);
  const revResidu = inkomsten - sum(rev) - sum(oi);
  const opexResidu = num(row.operating_costs) - sum(opx);
  const vat_revenue = vatOver(rev, ctx.rates.revenue, d) + vatOver(oi, ctx.rates.other_income, d) + revResidu * d / 100;
  let vat_opex = 0, vat_opex_deductible = 0;
  for (const it of opx) {
    const v = num(it.amount) * (ctx.rates.opex[it.name] ?? d) / 100;
    vat_opex += v; vat_opex_deductible += v * aftrekFraction(ctx.aftrek[it.name], ctx);
  }
  const vr = opexResidu * d / 100;
  vat_opex += vr; vat_opex_deductible += vr * aftrekFraction(ctx.defaultAftrek, ctx);
  const vat_nondeductible = vat_opex - vat_opex_deductible;
  const vat_balance = vat_revenue - vat_opex_deductible;
  const mixed = ctx.regime === "gemengd";
  const net = inkomsten - num(row.operating_costs) - num(row.depreciation) - num(row.interest_paid) + num(row.interest_income);
  // IJkpunt: + = kastoename (teruggave) in het veld; hier + = te betalen → teken omdraaien.
  const raw = row.btw_afdracht_teruggave;
  const vat_actual = raw == null || Number(raw) === 0 ? null : -Number(raw);
  return {
    pro_rata_pct: mixed ? ctx.proRata * 100 : null,
    vat_revenue, vat_opex, vat_opex_deductible, vat_nondeductible, vat_balance,
    net_after_vat: mixed ? net - vat_nondeductible : null,
    vat_actual, vat_diff: vat_actual === null ? null : vat_balance - vat_actual,
  };
}

export const BTW_MIXED_ROWS = [
  ["pro_rata_pct", "Pro-rata-percentage (aftrek op havenkosten)"],
  ["vat_revenue", "BTW af te dragen over belaste inkomsten"],
  ["vat_opex", "Voorbelasting over kosten"],
  ["vat_opex_deductible", "waarvan aftrekbaar"],
  ["vat_nondeductible", "Niet-aftrekbare BTW — kostenpost"],
  ["vat_balance", "BTW-saldo (schatting)"],
  ["net_after_vat", "Netto exploitatieresultaat na niet-aftrekbare BTW"],
];
export const BTW_MIXED_SCENARIO_ROWS = [
  ["pro_rata_pct", "Pro-rata-percentage (aftrek op havenkosten)"],
  ["vat_revenue", "BTW af te dragen over belaste inkomsten"],
  ["vat_opex", "Voorbelasting over kosten"],
  ["vat_opex_deductible", "waarvan aftrekbaar"],
  ["vat_nondeductible", "Niet-aftrekbare BTW op kosten (toegevoegd aan Tot. kosten en dus ook aan de kasstroom)"],
  ["vat_capex", "BTW over investeringen"],
  ["vat_capex_nondeductible", "waarvan niet-aftrekbaar (opgeteld bij de investering)"],
  ["vat_balance", "BTW-saldo (schatting)"],
];
export const btwBlockRows = (regime) => (regime === "gemengd" ? BTW_MIXED_ROWS : BTW_BLOCK_ROWS);
export const btwScenarioRows = (regime) => (regime === "gemengd" ? BTW_MIXED_SCENARIO_ROWS : BTW_SCENARIO_ROWS);
export const BTW_MIXED_NOTE = "Gemengd BTW-regime: belaste inkomsten dragen BTW af, vrijgestelde niet; de voorbelasting op kosten is per subpost volledig, pro rata (clubpercentage) of niet aftrekbaar. Het niet-aftrekbare deel is een echte kostenpost — in scenario's verwerkt in resultaat, kasstroom en investeringen; bij Voorgaande jaren en Begroting alleen in dit blok. Schatting op basis van één tarief per subpost; kwartaalritme van de aangifte buiten beschouwing.";

export const btwHint = (club) => {
  const r = btwRegime(club);
  if (r === "gemengd") return `Alle bedragen excl. BTW · gemengd BTW-regime (pro rata ${Number(club?.btw_pro_rata) || 0} %)`;
  if (r === "plichtig") return "Alle bedragen excl. BTW · BTW-plichtig";
  return "Alle bedragen incl. BTW · vrijgesteld van BTW";
};
